import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import Input from '../components/ui/Input';
import { User, Briefcase, Mail, Shield } from 'lucide-react';
import { useAuthStore } from '../store/useAuthStore';
import { fetchMerchantById } from '../services/api';
import { PERMISSIONS, hasPermission } from '../utils/permissions';

const ProfilePage = () => {
  const { user } = useAuthStore();
  const [loadingMerchant, setLoadingMerchant] = useState(false);
  const [merchant, setMerchant] = useState(null);
  const userPermissions = useMemo(() => user?.permissions || [], [user?.permissions]);

  useEffect(() => {
    const loadMerchantData = async () => {
      if (user?.merchantId && hasPermission(userPermissions, [PERMISSIONS.VIEW_SETTINGS, PERMISSIONS.MANAGE_SETTINGS], user?.role)) {
        setLoadingMerchant(true);
        try {
          const response = await fetchMerchantById(user.merchantId);
          if (response.data?.status === true || response.data?.success === true) {
            setMerchant(response.data.data);
          }
        } catch (err) {
          console.error('Error fetching merchant details:', err);
        } finally {
          setLoadingMerchant(false);
        }
      }
    };

    loadMerchantData();
  }, [user?.merchantId, userPermissions, user?.role]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Profile</h2>
        <p className="text-sm text-slate-500 mt-1">View your personal and organization profile details.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Personal Profile Card */}
        <Card className="lg:col-span-1 h-fit animate-in fade-in slide-in-from-left-2 duration-300">
          <CardHeader className="bg-slate-50/50 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg text-primary">
                <User size={20} />
              </div>
              <div>
                <CardTitle className="text-lg">Personal Info</CardTitle>
                <CardDescription>Your account identity</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="flex flex-col items-center pb-6 border-b border-slate-100">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center text-primary text-2xl font-bold mb-3 border-2 border-primary/20">
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </div>
              <h3 className="text-lg font-bold text-slate-900">{user?.firstName} {user?.lastName}</h3>
              <p className="text-sm text-slate-500">{user?.role}</p>
            </div>

            <div className="space-y-4 pt-4">
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <Mail size={16} className="text-slate-400" />
                <span className="truncate">{user?.email}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <Shield size={16} className="text-slate-400" />
                <span>Role: <span className="font-medium text-slate-900">{user?.role}</span></span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <Briefcase size={16} className="text-slate-400" />
                <span className="truncate">Merchant ID: <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{user?.merchantId}</span></span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Merchant Profile Card */}
        {hasPermission(userPermissions, [PERMISSIONS.VIEW_SETTINGS, PERMISSIONS.MANAGE_SETTINGS], user?.role) && (
          <Card className="lg:col-span-2 animate-in fade-in slide-in-from-right-2 duration-300">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary">
                    <Briefcase size={20} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Merchant Profile</CardTitle>
                    <CardDescription>Organization credentials and settlement info.</CardDescription>
                  </div>
                </div>
                {merchant?.status && (
                  <div className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                    merchant.status.toUpperCase() === 'ACTIVE'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {merchant.status}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-8 relative">
              {loadingMerchant && (
                <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center rounded-b-xl backdrop-blur-[1px]">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}

              <div className="space-y-10">
                {/* Organization Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div className="md:col-span-2">
                    <h3 className="text-sm font-bold text-slate-900 border-l-4 border-primary pl-3 mb-4">Organization Information</h3>
                  </div>
                  <Input
                    label="Business/Company Name"
                    value={merchant?.companyName || merchant?.clientName || ''}
                    readOnly
                    disabled
                    className="bg-slate-50 border-slate-100 shadow-none text-slate-600"
                  />
                  <Input
                    label="Merchant ID"
                    value={merchant?.merchantId || ''}
                    readOnly
                    disabled
                    className="bg-slate-50 border-slate-100 shadow-none text-slate-600"
                  />
                  <Input
                    label="Business Email"
                    value={merchant?.email || ''}
                    readOnly
                    disabled
                    className="bg-slate-50 border-slate-100 shadow-none text-slate-600"
                  />
                  <Input
                    label="Phone Number"
                    value={merchant?.phoneNumber || ''}
                    readOnly
                    disabled
                    className="bg-slate-50 border-slate-100 shadow-none text-slate-600"
                  />
                  <div className="md:col-span-2">
                    <Input
                      label="Registered Address"
                      value={merchant?.address || ''}
                      readOnly
                      disabled
                      className="bg-slate-50 border-slate-100 shadow-none text-slate-600"
                    />
                  </div>
                </div>

                {/* Settlement Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div className="md:col-span-2">
                    <h3 className="text-sm font-bold text-slate-900 border-l-4 border-primary pl-3 mb-4">Settlement Details</h3>
                  </div>
                  <Input
                    label="Waya Bank Account Number"
                    value={merchant?.wayaBankAccountNumber || ''}
                    readOnly
                    disabled
                    className="bg-slate-100 border-slate-100 shadow-none text-primary font-mono font-bold"
                  />
                  <Input
                    label="Account Holder Name"
                    value={merchant?.accountHolderName || ''}
                    readOnly
                    disabled
                    className="bg-slate-50 border-slate-100 shadow-none text-slate-600"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
